import { useCallback, useEffect, useMemo } from 'react';

import { usePrevious } from '@adsk/alloy-react-helpers';

import { isGroupHeader } from '../helpers';
import { GenericItem, InputItemGroup, GroupHeader } from '../types';

const useCollapsibleGroups = <TItem extends GenericItem>({
  allowCollapsibleGroups,
  flatItems,
  maybeGroupedItems,
  highlightedIndex,
  menuItems,
  expandedGroupKey,
  setExpandedGroupKey,
}: {
  allowCollapsibleGroups?: boolean;
  flatItems: (TItem | InputItemGroup<TItem>)[];
  maybeGroupedItems: TItem[] | InputItemGroup<TItem>[];
  highlightedIndex: number;
  menuItems: InputItemGroup<TItem>[] | undefined extends InputItemGroup<TItem>[]
    ? (GroupHeader | TItem)[]
    : TItem[];
  expandedGroupKey: string;
  setExpandedGroupKey: (
    value: ((prevState: string) => string) | string,
  ) => void;
}) => {
  const hasGroupHeaders = useMemo(
    () => allowCollapsibleGroups && menuItems.some(isGroupHeader),
    [menuItems, allowCollapsibleGroups],
  );
  const prevHighlightedIndex = usePrevious(highlightedIndex);
  const grouplessItems = useMemo(() => {
    if (hasGroupHeaders) {
      return flatItems.filter((item) => !isGroupHeader(item));
    }

    return flatItems;
  }, [hasGroupHeaders, flatItems]);

  const setExpandedGroup = useCallback(
    (key: string) => {
      if (key === expandedGroupKey) {
        setExpandedGroupKey('');
      } else {
        setExpandedGroupKey(key);
      }
    },
    [expandedGroupKey, setExpandedGroupKey],
  );

  // monitor highlightedIndex and expand the group of the highlighted item if necessary
  // this is for keyboard navigation of items
  useEffect(() => {
    if (
      allowCollapsibleGroups &&
      hasGroupHeaders &&
      highlightedIndex !== undefined &&
      highlightedIndex !== null &&
      highlightedIndex !== prevHighlightedIndex &&
      highlightedIndex >= 0
    ) {
      const item = grouplessItems[highlightedIndex];

      const group = (maybeGroupedItems as InputItemGroup<TItem>[])?.find(
        (itemGroup: InputItemGroup<TItem>) =>
          itemGroup.items.some((groupItem) => groupItem === item),
      );
      const groupWithKey = menuItems?.find(
        (menuItem) => group?.label === menuItem.label,
      );

      if (groupWithKey && expandedGroupKey !== groupWithKey.groupKey) {
        setExpandedGroupKey(groupWithKey.groupKey as string);
      }
    }
  }, [
    allowCollapsibleGroups,
    maybeGroupedItems,
    grouplessItems,
    highlightedIndex,
    menuItems,
    prevHighlightedIndex,
    expandedGroupKey,
    setExpandedGroupKey,
    hasGroupHeaders,
  ]);

  return {
    setExpandedGroup,
  };
};

export default useCollapsibleGroups;
